'use client'

import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useAuthContext } from '../contexts/AuthContext'
import { useT } from '../contexts/LocaleContext'
import {
  useDreamTeamById,
  useMyDreamTeamVote,
  useVoteDreamTeam,
} from '../hooks/useFootball'
import DreamTeamPitch from '../components/football/DreamTeamPitch'
import { showToast } from '../components/ui/Toast'

export default function DreamTeamViewPage() {
  const navigate = useNavigate()
  const t = useT()
  const { user } = useAuthContext()
  const { teamId: teamIdParam } = useParams<{ teamId: string }>()
  const teamId = Number(teamIdParam) || 0
  const { data, isLoading, error } = useDreamTeamById(teamId)
  const { data: myVoteData } = useMyDreamTeamVote(teamId)
  const vote = useVoteDreamTeam()

  const [selectedRating, setSelectedRating] = useState(0)
  const [hoverRating, setHoverRating] = useState(0)

  const team = data?.dream_team
  const votingOpen = data?.voting_open ?? false
  const myVote = myVoteData?.vote
  const isOwner = !!user && !!team && user.id === team.user_id

  useEffect(() => {
    if (myVote) setSelectedRating(myVote.rating)
  }, [myVote])

  const handleVote = async (rating: number) => {
    if (!team) return
    if (isOwner) {
      showToast(t('dreamTeam.voting.toastCannotVoteOwn'), 'error')
      return
    }
    if (!votingOpen) {
      showToast(t('dreamTeam.voting.toastPollClosed'), 'error')
      return
    }
    try {
      await vote.mutateAsync({ teamId: team.id, rating })
      setSelectedRating(rating)
      showToast(t('dreamTeam.voting.toastVoteRecorded'), 'success')
    } catch (err: any) {
      showToast(err.response?.data?.message || t('dreamTeam.voting.toastVoteError'), 'error')
    }
  }

  if (isLoading) {
    return (
      <div className="lucky-fb mini-app-container pb-24 min-h-screen flex items-center justify-center">
        <div className="lucky-fb__empty">{t('common.loading')}</div>
      </div>
    )
  }

  if (error || !team) {
    return (
      <div className="lucky-fb mini-app-container pb-24 min-h-screen">
        <div className="lucky-fb__inner">
          <div className="lucky-fb__card lucky-fb__empty">{t('dreamTeam.view.notFound')}</div>
          <button
            type="button"
            className="lucky-fb__cta"
            onClick={() => navigate('/football/dream-teams/explore')}
          >
            {t('dreamTeam.view.backToExplore')}
          </button>
        </div>
      </div>
    )
  }

  const matchLabel = team.match
    ? t('dreamTeam.matchVs', { home: team.match.home_team, away: team.match.away_team })
    : null
  const avg = team.avg_rating || 0
  const votes = team.vote_count || 0
  const creatorName = team.user
    ? `${[team.user.first_name, team.user.last_name].filter(Boolean).join(' ')}${
        team.user.username ? ` (@${team.user.username})` : ''
      }`.trim()
    : ''

  return (
    <div className="lucky-fb mini-app-container pb-28 min-h-screen">
      <div className="lucky-fb__inner">
        <header className="lucky-fb__header">
          <button type="button" className="lucky-wallet__back" onClick={() => navigate(-1)} aria-label={t('common.back')}>
            →
          </button>
          <div>
            <h1>{t('dreamTeam.title')}</h1>
            {matchLabel && <p>{matchLabel}</p>}
          </div>
        </header>

        <section className="lucky-fb__card">
          <label className="lucky-wallet__label">
            {t('dreamTeam.view.labelTeamName')}
            <div className="lucky-wallet__input" style={{ cursor: 'default' }}>
              {team.name}
            </div>
          </label>
          <label className="lucky-wallet__label" style={{ marginTop: 10 }}>
            {t('dreamTeam.view.labelFormation')}
            <div className="lucky-wallet__input" style={{ cursor: 'default' }}>
              {team.formation}
            </div>
          </label>
        </section>

        <DreamTeamPitch
          formation={team.formation}
          players={team.players || []}
          boardText={
            matchLabel ? `${team.name} · ${matchLabel}` : `${team.name} · ${team.formation}`
          }
        />

        <section className="lucky-fb__card">
          <div className="lucky-side__meta-row">
            <span className={`lucky-side__chip ${team.is_submitted ? 'is-ok' : ''}`}>
              {team.is_submitted ? t('dreamTeam.status.submitted') : t('dreamTeam.status.draft')}
            </span>
            <strong>
              {t('dreamTeam.view.metaRating', {
                avg: avg.toFixed(1),
                votes,
                totalScore: team.total_score,
              })}
            </strong>
          </div>
          {team.user && (
            <p className="lucky-fb__hint" style={{ marginBottom: 0, marginTop: 8 }}>
              {t('dreamTeam.view.creator', { name: creatorName })}
            </p>
          )}
        </section>

        <section className="lucky-fb__card">
          <h2 className="lucky-fb__section-title">{t('dreamTeam.voting.sectionTitle')}</h2>
          {isOwner ? (
            <p className="lucky-fb__empty">{t('dreamTeam.voting.ownerHint')}</p>
          ) : myVote ? (
            <div className="lucky-side__stars-block">
              <p className="is-ok">{t('dreamTeam.voting.alreadyVoted')}</p>
              <div className="lucky-side__stars">
                {[1, 2, 3, 4, 5].map((star) => (
                  <span key={star} className={star <= myVote.rating ? 'is-on' : ''}>
                    ★
                  </span>
                ))}
              </div>
              <em>{t('dreamTeam.voting.yourRating', { rating: myVote.rating })}</em>
            </div>
          ) : votingOpen ? (
            <div className="lucky-side__stars lucky-side__stars--pick">
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  key={star}
                  type="button"
                  className={selectedRating >= star || hoverRating >= star ? 'is-on' : ''}
                  onClick={() => handleVote(star)}
                  onMouseEnter={() => setHoverRating(star)}
                  onMouseLeave={() => setHoverRating(0)}
                  disabled={vote.isPending}
                >
                  ★
                </button>
              ))}
            </div>
          ) : (
            <div className="lucky-fb__empty">
              {t('dreamTeam.voting.pollClosed')}
              <span>{t('dreamTeam.voting.pollClosedHint')}</span>
            </div>
          )}
        </section>

        <div className="lucky-side__actions">
          <button
            type="button"
            className="lucky-wallet__btn-sec"
            onClick={() => navigate('/football/dream-teams/explore')}
          >
            {t('dreamTeam.explore.exploreTeams')}
          </button>
          {team.match_id > 0 && (
            <button
              type="button"
              className="lucky-fb__cta"
              onClick={() => navigate(`/football/dream-team/${team.match_id}`)}
            >
              {t('dreamTeam.view.buildMyTeam')}
            </button>
          )}
        </div>
      </div>
    </div>
  )
}
